import React, { useState, useEffect } from 'react';
import { X, Share2, Search, UserPlus, Trash2, Eye, Edit3 } from 'lucide-react';
import { apiService } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';
import ConfirmModal from './ConfirmModal';

const ShareListModal = ({ list, onClose, onUpdate }) => {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('read');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingPermissions, setEditingPermissions] = useState({});
  const [removeAccessConfirm, setRemoveAccessConfirm] = useState({ show: false, userId: null, userEmail: '' });
  const { t } = useLanguage();

  useEffect(() => {
    // ESC key handler
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  useEffect(() => {
    // Debounced search
    if (email.length > 2) {
      const timer = setTimeout(async () => {
        setSearchLoading(true);
        try {
          const results = await apiService.searchUsers(email);
          setSearchResults(results);
        } catch (err) {
          console.error('Search error:', err);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [email]);

  const handleShare = async (userEmail = email) => {
    if (!userEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await apiService.shareList(list.id, userEmail, permission);
      setSuccessMessage(`List shared with ${userEmail}`);
      setEmail('');
      setSearchResults([]);
      
      // Refresh list data
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to share list');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAccess = async (userId, userEmail) => {
    setRemoveAccessConfirm({ show: true, userId, userEmail });
  };

  const confirmRemoveAccess = async () => {
    const { userId, userEmail } = removeAccessConfirm;
    
    try {
      await apiService.removeListAccess(list.id, userId);
      setSuccessMessage(t('accessRemoved', `Access removed for ${userEmail}`));
      
      // Refresh list data
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message || t('failedToRemoveAccess', 'Failed to remove access'));
    }
  };

  const handleChangePermission = async (userId, newPermission, userEmail) => {
    try {
      await apiService.updateListPermission(list.id, userId, newPermission);
      setSuccessMessage(t('permissionUpdated', `Permission updated for ${userEmail}`));
      setEditingPermissions(prev => ({ ...prev, [userId]: false }));
      
      // Refresh list data
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message || t('failedToUpdatePermission', 'Failed to update permission'));
    }
  };

  const handleUserSelect = (user) => {
    setEmail(user.email);
    setSearchResults([]);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <Share2 size={24} />
            <h2>{t('shareList', 'Share')} "{list.name}"</h2>
          </div>
          <button onClick={onClose} className="modal-close">
            <X size={20} />
          </button>
        </div>


        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}

          <div className="shared-users-section">
            <h3>{t('peopleWithAccess', 'People with Access')}</h3>
            <div className="shared-users-list">
              {list.sharedWith && list.sharedWith.length > 0 ? (
                list.sharedWith.map(share => (
                  <div key={share.user.id} className="shared-user-item">
                    <div className="user-details">
                      <div className="user-name">{share.user.name}</div>
                      <div className="user-email">{share.user.email}</div>
                    </div>
                    <div className="permission-controls">
                      {editingPermissions[share.user.id] ? (
                        <div className="permission-edit">
                          <select
                            value={share.permission}
                            onChange={(e) => handleChangePermission(share.user.id, e.target.value, share.user.email)}
                            className="permission-select"
                          >
                            <option value="read">{t('viewOnly', 'View Only')}</option>
                            <option value="write">{t('canEdit', 'Can Edit')}</option>
                          </select>
                        </div>
                      ) : (
                        <div className="permission-info">
                          {share.permission === 'read' ? (
                            <button
                              onClick={() => setEditingPermissions(prev => ({ ...prev, [share.user.id]: true }))}
                              className="permission-badge read clickable"
                              title={t('changePermission', 'Change permission')}
                            >
                              <Eye size={14} />
                              {t('viewOnly', 'View Only')}
                            </button>
                          ) : (
                            <button
                              onClick={() => setEditingPermissions(prev => ({ ...prev, [share.user.id]: true }))}
                              className="permission-badge write clickable"
                              title={t('changePermission', 'Change permission')}
                            >
                              <Edit3 size={14} />
                              {t('canEdit', 'Can Edit')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveAccess(share.user.id, share.user.email)}
                      className="remove-access-btn"
                      title={t('removeAccess', 'Remove access')}
                      style={{ color: '#d32f2f', marginLeft: 8 }}
                    >
                      <Trash2 size={16} /> {t('remove', 'Remove')}
                    </button>
                  </div>
                ))
              ) : (
                <div className="no-shared-users">{t('noUsersWithAccess', 'No users currently have access to this list.')}</div>
              )}
            </div>
          </div>

          <div className="share-form-section">
            <h3>{t('addPeople', 'Add People')}</h3>
            <div className="share-form">
              <div className="form-group">
                <label htmlFor="userEmail">{t('emailAddress', 'Email Address')}</label>
                <div className="search-container">
                  <div className="input-with-icon">
                    <Search size={18} />
                    <input
                      id="userEmail"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('enterEmailAddress', 'Enter email address')}
                      className="form-input"
                    />
                  </div>
                  
                  {searchResults.length > 0 && (
                    <div className="search-results">
                      {searchResults.map(user => (
                        <div
                          key={user.id}
                          className="search-result-item"
                          onClick={() => handleUserSelect(user)}
                        >
                          <div className="user-info">
                            <strong>{user.name}</strong>
                            <span>{user.email}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {searchLoading && (
                    <div className="search-loading">{t('searching', 'Searching...')}</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="permission">{t('permission', 'Permission')}</label>
                <select
                  id="permission"
                  value={permission}
                  onChange={(e) => setPermission(e.target.value)}
                  className="form-select"
                >
                  <option value="read">{t('viewOnly', 'View Only')}</option>
                  <option value="write">{t('editAndAddItems', 'Edit & Add Items')}</option>
                </select>
              </div>

              <button
                onClick={() => handleShare()}
                disabled={loading || !email.trim()}
                className="btn-primary share-btn"
              >
                {loading ? t('sharing', 'Sharing...') : t('shareList', 'Share List')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={removeAccessConfirm.show}
        onClose={() => setRemoveAccessConfirm({ show: false, userId: null, userEmail: '' })}
        onConfirm={confirmRemoveAccess}
        title={t('removeAccess', 'Remove Access')}
        message={t('confirmRemoveAccess', `Remove access for ${removeAccessConfirm.userEmail}?`)}
        confirmText={t('removeAccess', 'Remove Access')}
        danger={true}
      />
    </div>
  );
};

export default ShareListModal;
